import { type FC } from "react";
import { useTranslation } from "react-i18next";

export type TrendMetricKey = "spend" | "impressions" | "clicks" | "addToCart" | "conversions";
export type TrendGranularity = "daily" | "monthly";

/** Matches Meta Ads dashboard UI/UX (performance trend + funnel + grouped bars). */
export const META_CHART_METRIC_COLORS = {
  spend: "#2b74e4",
  impressions: "#36cdb0",
  clicks: "#f6c15f",
  addToCart: "#ff7a59",
  conversions: "#8d4fd3",
  ctr: "#f6c15f",
} as const;

/**
 * Impressions/spend run in the tens-to-hundreds-of-thousands while clicks/add-to-cart/
 * conversions are two-to-three orders of magnitude smaller — on a single axis the latter
 * flatten to an invisible line at zero, so they get their own right-hand scale.
 */
export const META_TREND_AXIS_ID: Record<TrendMetricKey, "primary" | "secondary"> = {
  spend: "primary",
  impressions: "primary",
  clicks: "secondary",
  addToCart: "secondary",
  conversions: "secondary",
};

/** Grouped bar charts (platform / post type) use green for conversions per UI spec. */
export const META_GROUPED_BAR_COLORS = {
  spend: META_CHART_METRIC_COLORS.spend,
  impressions: META_CHART_METRIC_COLORS.impressions,
  conversions: META_CHART_METRIC_COLORS.conversions,
  ctr: META_CHART_METRIC_COLORS.ctr,
} as const;

export type MetaBarMetricKey = keyof typeof META_GROUPED_BAR_COLORS;
export type MetaGroupedChartMetricKey = "spend" | "impressions" | "conversions" | "ctr";
export type MetaGroupedChartDatum = {
  label: string;
  spend: number;
  impressions: number;
  conversions: number;
  ctr: number;
  spendValue: number;
  impressionsValue: number;
  conversionsValue: number;
  ctrValue: number;
};

const META_AXIS_LABEL_MAX_LENGTH = 12;

export const truncateMetaChartLabel = (
  value: string | number | null | undefined,
  maxLength = META_AXIS_LABEL_MAX_LENGTH
) => {
  const label = String(value ?? "").trim();
  if (label.length <= maxLength) {
    return label;
  }
  return `${label.slice(0, Math.max(1, maxLength - 1))}...`;
};

export const MetaGroupedBarLegend: FC<{
  items: ReadonlyArray<{ key: MetaBarMetricKey; label: string }>;
}> = ({ items }) => {
  const { t } = useTranslation();
  return (
  <ul className="paid-media-chart-legend" aria-label={t("metaAdsReport.charts.legendAria")}>
    {items.map(item => (
      <li key={item.key} className="paid-media-chart-legend__item">
        <span
          className="paid-media-chart-legend__swatch"
          style={{ backgroundColor: META_GROUPED_BAR_COLORS[item.key] }}
          aria-hidden
        />
        {item.label}
      </li>
    ))}
  </ul>
  );
};
